import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ClockCounterClockwise, User, Funnel } from "@phosphor-icons/react";

const ACTION_LABELS = {
  "job.created": { text: "created job", color: "bg-emerald-100 text-emerald-800 border-emerald-400" },
  "job.updated": { text: "edited job", color: "bg-blue-100 text-blue-800 border-blue-400" },
  "job.clock_in": { text: "clocked in", color: "bg-cyan-100 text-cyan-800 border-cyan-400" },
  "job.clock_out": { text: "clocked out", color: "bg-slate-100 text-slate-800 border-slate-400" },
  "photo.before.uploaded": { text: "uploaded a Before photo", color: "bg-amber-100 text-amber-800 border-amber-400" },
  "photo.after.uploaded": { text: "uploaded an After photo", color: "bg-amber-100 text-amber-800 border-amber-400" },
  "photo.receipt.uploaded": { text: "uploaded a receipt", color: "bg-amber-100 text-amber-800 border-amber-400" },
  "photo.annotated.uploaded": { text: "uploaded a marked-up photo", color: "bg-amber-100 text-amber-800 border-amber-400" },
  "user.updated": { text: "updated a team member", color: "bg-violet-100 text-violet-800 border-violet-400" },
  "user.deleted": { text: "deleted a team member", color: "bg-red-100 text-red-800 border-red-400" },
};

export default function AdminActivity() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ user_id: "", entity_type: "" });
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.user_id) params.set("user_id", filter.user_id);
      if (filter.entity_type) params.set("entity_type", filter.entity_type);
      const { data } = await api.get(`/activity?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error("Failed to load activity"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.user_id, filter.entity_type]);
  useEffect(() => { api.get("/users").then(r => setWorkers(r.data)).catch(() => {}); }, []);

  if (user && user.role !== "admin" && user.platform_role !== "super_admin") {
    return <Navigate to="/worker" replace />;
  }

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-5xl">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-[#0f172a] text-white flex items-center justify-center">
            <ClockCounterClockwise weight="bold" size={22}/>
          </div>
          <div>
            <h1 className="font-display font-black text-4xl tracking-tight">Activity Log</h1>
            <p className="text-slate-500 font-medium text-sm">Everyone's actions across all jobs — for accountability.</p>
          </div>
        </div>

        <div className="mt-6 rugged-card p-4 flex flex-wrap gap-2 items-center" data-testid="activity-filters">
          <Funnel weight="bold" size={16} className="text-slate-500"/>
          <span className="text-xs font-black uppercase tracking-wider text-slate-500">Filter:</span>
          <select value={filter.user_id} onChange={e => setFilter({ ...filter, user_id: e.target.value })}
            data-testid="activity-filter-user"
            className="h-9 px-2 border-2 border-[#0f172a] font-medium text-sm bg-white">
            <option value="">Everyone</option>
            {workers.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <select value={filter.entity_type} onChange={e => setFilter({ ...filter, entity_type: e.target.value })}
            data-testid="activity-filter-type"
            className="h-9 px-2 border-2 border-[#0f172a] font-medium text-sm bg-white">
            <option value="">All types</option>
            <option value="job">Jobs</option>
            <option value="user">Team</option>
          </select>
          {(filter.user_id || filter.entity_type) && (
            <button onClick={() => setFilter({ user_id: "", entity_type: "" })}
              className="text-xs font-black uppercase text-[#00C4CC] hover:underline">
              Clear
            </button>
          )}
        </div>

        <div className="mt-4 rugged-card divide-y divide-slate-200" data-testid="activity-list">
          {loading && <div className="p-6 text-center text-slate-500 font-medium">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-6 text-center text-slate-400 font-medium">No activity yet. Actions like creating jobs, clocking in, and uploading photos will appear here.</div>
          )}
          {items.map(a => {
            const meta = ACTION_LABELS[a.action] || { text: a.action.replace(/_/g, " "), color: "bg-slate-100 text-slate-800 border-slate-400" };
            const isJob = a.entity_type === "job" && a.entity_id;
            return (
              <div key={a.id} className="p-4 flex items-start gap-3" data-testid={`activity-row-${a.id}`}>
                <div className="h-10 w-10 flex-shrink-0 bg-[#f4f4f5] border-2 border-slate-300 flex items-center justify-center">
                  <User weight="bold" size={18}/>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-black text-sm">{a.user_name || "Someone"}</span>
                    {a.user_office_role && (
                      <span className="text-[10px] font-black uppercase text-slate-500 tracking-widest">{a.user_office_role}</span>
                    )}
                    <span className={`inline-block px-2 py-0.5 text-[11px] font-black uppercase border-2 ${meta.color}`}>
                      {meta.text}
                    </span>
                  </div>
                  {a.entity_label && (
                    <div className="mt-1 text-sm text-slate-700">
                      {isJob ? (
                        <Link to={`/admin/jobs/${a.entity_id}`} className="font-bold hover:underline text-[#0f172a]">
                          {a.entity_label}
                        </Link>
                      ) : <span className="font-bold">{a.entity_label}</span>}
                    </div>
                  )}
                  <div className="mt-1 text-[11px] text-slate-500 font-mono">{new Date(a.created_at).toLocaleString()}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminShell>
  );
}
